import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'

const STATUS_COLORS: Record<string, string> = {
  ASSIGNED: 'bg-blue-100 text-blue-800',
  IN_PROGRESS: 'bg-orange-100 text-orange-800',
  STARTED: 'bg-orange-100 text-orange-800',
  PICKED_UP: 'bg-amber-100 text-amber-800',
  IN_TRANSIT: 'bg-orange-100 text-orange-800',
  DELIVERED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-gray-100 text-gray-600',
}

export default function DriverJobsPage() {
  const { data: jobs, isLoading } = useQuery({
    queryKey: ['driver-jobs'],
    queryFn: async () => { const r = await api.jobs.list({ viewer: 'driver' }); return r.data },
    refetchInterval: 10000,
  })

  const activeJobs = jobs?.filter(j => j.status !== 'DELIVERED' && j.status !== 'CANCELLED') ?? []
  const completedJobs = jobs?.filter(j => j.status === 'DELIVERED' || j.status === 'CANCELLED') ?? []

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">My Jobs</h1>
        <p className="text-sm text-gray-500 mt-1">Use each job card as a checklist: review docs, open trailer loader, and complete POD.</p>
      </div>

      {isLoading && <div className="text-gray-400 text-sm">Loading…</div>}

      {!isLoading && jobs?.length === 0 && (
        <div className="bg-white rounded-xl border text-center py-16">
          <p className="text-gray-400">No jobs assigned yet.</p>
          <p className="text-gray-300 text-sm mt-1">Your dispatcher will assign you to a job.</p>
        </div>
      )}

      {activeJobs.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Active</h2>
          <div className="space-y-3">
            {activeJobs.map(job => (
              <div key={job.id} className="block bg-white rounded-xl border-2 border-blue-200 hover:border-blue-400 transition p-4">
                <div className="flex justify-between items-center gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[job.status] ?? 'bg-gray-100'}`}>
                        {job.status}
                      </span>
                      {job.jobNumber && <span className="text-xs text-gray-400 font-mono">{job.jobNumber}</span>}
                    </div>
                    <div className="text-sm font-medium text-gray-900">Job ID: <span className="font-mono">{job.id.slice(0, 8)}</span></div>
                    {job.assignedAt && (
                      <div className="text-xs text-gray-400 mt-0.5">
                        Assigned {new Date(job.assignedAt).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                  <Link to={`/driver/jobs/${job.id}`} className="text-blue-700 font-bold text-sm">Open {'->'}</Link>
                </div>

                <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                  <div className="text-sm font-medium text-slate-900">
                    {job.origin && job.destination ? `${job.origin} → ${job.destination}` : 'Route not yet loaded'}
                  </div>
                  <div className="mt-1 text-xs text-slate-600">
                    {job.vehicles?.[0]
                      ? [job.vehicles[0].year, job.vehicles[0].make, job.vehicles[0].model].filter(Boolean).join(' ') || job.vehicles[0].vin
                      : 'Vehicle not yet loaded'}
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  <Link to={`/driver/jobs/${job.id}`} className="rounded-lg border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50">
                    Open Job
                  </Link>
                  <Link to={`/driver/jobs/${job.id}/trailer`} className="rounded-lg border border-blue-300 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-800 hover:bg-blue-100">
                    Open Trailer Loader
                  </Link>
                  <Link to={`/jobs/${job.id}/pod`} className="rounded-lg border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50">
                    Upload/View POD
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {completedJobs.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Completed</h2>
          <div className="space-y-2">
            {completedJobs.map(job => (
              <Link key={job.id} to={`/driver/jobs/${job.id}`}
                className="block bg-white rounded-xl border hover:border-gray-300 transition p-3">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[job.status] ?? 'bg-gray-100'}`}>
                      {job.status}
                    </span>
                    <span className="text-xs text-gray-500 font-mono">{job.jobNumber ?? job.id.slice(0, 8)}</span>
                  </div>
                  {job.completedAt && (
                    <span className="text-xs text-gray-400">{new Date(job.completedAt).toLocaleDateString()}</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
